import {
  isSessionDeletePending,
  type RegistryDatasetRef,
  type TrainingSessionListView,
} from "@/api";
import { TableRow, type TableRowActionBuilder } from "@carbon6/c6-frontend-design";
import {
  formatSessionCreatedAt,
  formatSessionNote,
  formatSessionStatus,
  sessionDatasetExtraCount,
  sessionDatasetLabel,
  sessionDisplayName,
  sessionExperimentLabel,
  sessionHasNote,
  sessionWorkspaceLabel,
} from "./trainingSessionFormat";

export type SubmitSessionRowClickHandler = (view: TrainingSessionListView) => void;

export type SubmitSessionRowActionBuilder = TableRowActionBuilder<SubmitSessionTableRow>;

// 세션 테이블의 한 행. 내 목록과 팀 목록이 같은 서버 row(TrainingSessionListView)를 쓴다. 표시
// 파생값을 평평하게 두고, 원본 row 는 행 클릭(상세 Window 열기)에 그대로 전달한다.
// registryUserLabel(팀 목록의 멤버 이름)과 workspaceLabel(내 목록)은 각각 한쪽 컬럼에만 보인다.
// 그래도 행은 두 값을 항상 계산한다. 소유 판별은 상세 컨트롤러가 한다.
export class SubmitSessionTableRow extends TableRow {
  readonly id: string;
  readonly registryUserLabel: string;
  readonly displayName: string;
  readonly experimentLabel: string;
  readonly workspaceLabel: string;
  readonly status: string;
  readonly statusLabel: string;
  readonly noteLabel: string;
  // 노트 셀은 표식만 표시하고 본문은 hover 로 연다. 그래서 표식 유무는 본문 문자열이 아니라 이 값으로 판정한다.
  readonly hasNote: boolean;
  readonly createdLabel: string;
  // 대표 입력 하나와 나머지 개수. 참조가 있으면 셀이 섬네일과 링크를 표시한다(없으면 평문).
  readonly datasetLabel: string;
  readonly datasetRef: RegistryDatasetRef | null;
  // 대표 밖 입력 수. 이름이 잘릴 때 이 수가 잘리지 않도록 셀이 이름과 따로 표시한다.
  readonly datasetExtraCount: number;
  // 삭제 예정 여부. 상태와 독립인 표시 축이라 status 를 덮지 않고 이름 앞 칩으로 따로 표시한다.
  // 서버가 이 필드를 보내기 전 배포 기간에는 필드가 없어서 모두 false 로 읽는다.
  readonly deleted: boolean;
  private readonly view: TrainingSessionListView;
  private readonly clickHandler: SubmitSessionRowClickHandler;

  constructor(
    view: TrainingSessionListView,
    clickHandler: SubmitSessionRowClickHandler,
    rowActionsBuilder: SubmitSessionRowActionBuilder | null = null,
  ) {
    super(rowActionsBuilder ? (row) => rowActionsBuilder(row as SubmitSessionTableRow) : null);
    this.view = view;
    this.clickHandler = clickHandler;
    this.id = view.id;
    // 컬럼은 멤버 이름만 표시한다. 그래서 소유자 없는 행은 다른 빈 값과 같은 표기를 쓴다.
    this.registryUserLabel = view.registry_user?.display_name ?? "—";
    this.displayName = sessionDisplayName(view);
    this.experimentLabel = sessionExperimentLabel(view);
    this.workspaceLabel = sessionWorkspaceLabel(view);
    this.status = view.status;
    this.statusLabel = formatSessionStatus(view);
    this.noteLabel = formatSessionNote(view.note);
    this.hasNote = sessionHasNote(view.note);
    this.createdLabel = formatSessionCreatedAt(view.created_at);
    this.datasetLabel = sessionDatasetLabel(view);
    this.datasetRef = view.dataset?.registry_ref ?? null;
    this.datasetExtraCount = sessionDatasetExtraCount(view);
    this.deleted = isSessionDeletePending(view);
  }

  getRowId(): string {
    return this.id;
  }

  // 모든 행을 표시한다. 가시성은 서버 조회가 정한다.
  canShow(): boolean {
    return true;
  }

  // 비교 선택은 모든 상태를 허용한다. 진행 중 세션을 완료 세션과 비교하는 것이 핵심 사용 사례라
  // 행 자격으로 거르지 않는다. 지표 없는 세션은 비교 창이 placeholder 로 표시한다. 상한은
  // source 의 canSelectMore 가 정한다.
  canSelect(): boolean {
    return true;
  }

  // 비교 창에 전달할 원본 목록 행. 상세가 도착하기 전 범례와 상태 칩을 표시하는 스냅샷이다.
  getView(): TrainingSessionListView {
    return this.view;
  }

  override get isRowClickable(): boolean {
    return true;
  }

  override onRowClick(): void {
    this.clickHandler(this.view);
  }
}
